// @ts-nocheck
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import DialogTransition from './Transitions/DialogTransition';

/**
 * @function DeleteConfirmModal
 * @description to show the Modal of Delete Node Confirm
 * @param {function} onCloseClick - A function to handle modal close
 * @param {boolean} open - A boolean value
 * @param {function} onDeleteClick - A function on click to handle delete
 * @param {object} activeProjectConfirmationMsg - get the confirmation message to delete the active project.
 * @param {object} confirmationMsg - get title of this modal
 * @param {string} resourceName - type of node
 * @returns {JSX.Element} DeleteConfirmModal UI
 * @example <DeleteConfirmModal onCloseClick={onCloseClick} />
 */
const DeleteConfirmModal = ({
  onCloseClick,
  open,
  onDeleteClick,
  activeProjectConfirmationMsg,
  confirmationMsg,
  resourceName,
}) => (
  <Dialog
    open={open}
    aria-labelledby="delete-dialog"
    maxWidth="xs"
    fullWidth
    onClose={onCloseClick}
    TransitionComponent={DialogTransition}
  >
    <DialogTitle id="delete-dialog">
      {resourceName ? `Delete ${resourceName}` : ''}
    </DialogTitle>
    <IconButton
      color="primary"
      aria-label="Close"
      onClick={onCloseClick}
      size="large"
      sx={{
        position: 'absolute',
        right: 16,
        top: 8,
      }}
    >
      <CloseIcon />
    </IconButton>
    <DialogContent dividers>
      <Grid container spacing={2}>
        {(activeProjectConfirmationMsg || resourceName === 'Project') && (
          <Grid item xs={12}>
            <Typography
              variant="body4"
              tabIndex={0}
              role="note"
              aria-label={`Important note: ${
                activeProjectConfirmationMsg
                  ? 'You are about to delete the project that is active.'
                  : ''
              } ${
                resourceName === 'Project'
                  ? 'You might lose access to the artifacts like datasets and documents in this project. Make sure you have downloaded or shared those with other users before proceeding with the deletion.'
                  : ''
              }`}
              sx={{
                fontStyle: 'normal',
                border: 'none',
                '&:focus-visible': {
                  border: '2px solid',
                  borderColor: 'primary.main',
                },
              }}
            >
              <strong>Note:</strong>&nbsp;
              <i>
                {activeProjectConfirmationMsg && (
                  <>You are about to delete the project that is active. </>
                )}
                {resourceName === 'Project' && (
                  <>
                    You might lose access to the artifacts like datasets and
                    documents in this project. Make sure you have downloaded or
                    shared those with other users before proceeding with the
                    deletion.
                  </>
                )}
              </i>
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            align="center"
            sx={{
              wordWrap: 'break-word',
              '&:focus-visible': {
                border: '2px solid',
                borderColor: 'primary.main',
              },
            }}
            tabIndex={0}
            role="note"
            aria-label={confirmationMsg}
          >
            {confirmationMsg}
          </Typography>
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button
        color="primary"
        variant="outlined"
        title="No"
        aria-label="No"
        onClick={onCloseClick}
      >
        No
      </Button>
      <Button
        color="primary"
        variant="contained"
        title="Yes"
        aria-label="Yes"
        onClick={onDeleteClick}
      >
        Yes
      </Button>
    </DialogActions>
  </Dialog>
);

DeleteConfirmModal.defaultProps = {
  resourceName: '',
  activeProjectConfirmationMsg: false,
};
DeleteConfirmModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  activeProjectConfirmationMsg: PropTypes.bool,
  confirmationMsg: PropTypes.string.isRequired,
  resourceName: PropTypes.string,
};

export default DeleteConfirmModal;
